import { useEffect, useMemo, useState } from "react";
import type { Collection, KnowledgeNode } from "./KnowledgeWorkbench";

interface CollectionsPanelProps {
  kbId: string;
}

interface CollectionsResponse {
  collections?: Collection[];
}

interface NodesResponse {
  nodes?: KnowledgeNode[];
}

export function CollectionsPanel({ kbId }: CollectionsPanelProps) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [nodes, setNodes] = useState<KnowledgeNode[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const [collectionsResponse, nodesResponse] = await Promise.all([
      fetch(`/api/knowledge/${kbId}/collections`).then((response) => response.json() as Promise<CollectionsResponse>),
      fetch(`/api/knowledge/${kbId}/nodes`).then((response) => response.json() as Promise<NodesResponse>),
    ]);
    setCollections(collectionsResponse.collections || []);
    setNodes(nodesResponse.nodes || []);
  }

  useEffect(() => {
    load().catch(() => {
      setCollections([]);
      setNodes([]);
    });
  }, [kbId]);

  const filteredNodes = useMemo(() => {
    if (!filter.trim()) {
      return nodes;
    }
    const query = filter.toLowerCase();
    return nodes.filter((node) => {
      return (
        node.title.toLowerCase().includes(query) ||
        node.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }, [nodes, filter]);

  async function createCollection() {
    if (!name.trim()) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`/api/knowledge/${kbId}/collections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description,
          node_ids: selectedNodeIds,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to create collection");
      }
      setName("");
      setDescription("");
      setSelectedNodeIds([]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create collection");
    } finally {
      setSubmitting(false);
    }
  }

  function toggleNode(nodeId: string) {
    setSelectedNodeIds((current) =>
      current.includes(nodeId) ? current.filter((id) => id !== nodeId) : [...current, nodeId],
    );
  }

  return (
    <div style={S.root}>
      <div style={S.column}>
        <div style={S.card}>
          <div style={S.heading}>Curated Collections</div>
          <div style={S.subheading}>
            Package graph nodes into named bundles for restore, navigation, or downstream skills.
          </div>
          <input
            style={S.input}
            placeholder="Collection name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <textarea
            style={S.textarea}
            rows={4}
            placeholder="What does this collection represent?"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <div style={S.selectionMeta}>{selectedNodeIds.length} nodes selected</div>
          <button
            style={{ ...S.primaryBtn, opacity: !name.trim() || submitting ? 0.45 : 1 }}
            disabled={!name.trim() || submitting}
            onClick={createCollection}
          >
            {submitting ? "Saving..." : "Create Collection"}
          </button>
          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.card}>
          <div style={S.sectionLabel}>Existing Collections</div>
          {collections.length === 0 ? (
            <div style={S.empty}>No collections yet.</div>
          ) : (
            collections.map((collection) => (
              <div key={collection.id} style={S.collectionCard}>
                <div style={S.collectionName}>{collection.name}</div>
                <div style={S.collectionDesc}>{collection.description || "No description."}</div>
                <div style={S.collectionMeta}>{collection.node_ids.length} linked nodes</div>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionHeader}>
          <div>
            <div style={S.sectionLabel}>Node Picker</div>
            <div style={S.subheading}>Choose the nodes that belong in the collection.</div>
          </div>
          <input
            style={S.filter}
            placeholder="Filter nodes..."
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        <div style={S.nodeList}>
          {filteredNodes.length === 0 ? (
            <div style={S.empty}>No nodes available. Index sources first.</div>
          ) : (
            filteredNodes.map((node) => {
              const selected = selectedNodeIds.includes(node.id);
              return (
                <button
                  key={node.id}
                  style={{
                    ...S.nodeRow,
                    ...(selected ? S.nodeRowSelected : {}),
                  }}
                  onClick={() => toggleNode(node.id)}
                >
                  <div style={S.nodeTitle}>{node.title}</div>
                  <div style={S.nodeMeta}>
                    {node.node_type}
                    {node.tags.length ? ` · ${node.tags.join(", ")}` : ""}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "grid",
    gridTemplateColumns: "360px 1fr",
    gap: "1rem",
    padding: "1rem",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  card: {
    padding: "1rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  heading: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "var(--ot-text-primary, #e6edf3)",
  },
  subheading: {
    marginTop: "0.25rem",
    fontSize: "0.72rem",
    color: "var(--ot-text-secondary, #8b949e)",
    lineHeight: 1.5,
  },
  input: {
    width: "100%",
    marginTop: "0.8rem",
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.55rem 0.65rem",
    fontSize: "0.78rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  textarea: {
    width: "100%",
    marginTop: "0.6rem",
    boxSizing: "border-box",
    resize: "vertical",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.65rem",
    fontSize: "0.78rem",
    lineHeight: 1.5,
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  selectionMeta: {
    marginTop: "0.55rem",
    marginBottom: "0.75rem",
    fontSize: "0.7rem",
    color: "var(--ot-text-secondary, #8b949e)",
  },
  primaryBtn: {
    width: "100%",
    padding: "0.6rem 0.8rem",
    borderRadius: "6px",
    border: "none",
    background: "var(--ot-accent, #80f3b2)",
    color: "var(--ot-bg-deep, #050608)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.75rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  error: {
    marginTop: "0.6rem",
    color: "#ff7b72",
    fontSize: "0.72rem",
  },
  sectionLabel: {
    fontSize: "0.68rem",
    color: "var(--ot-accent, #80f3b2)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  collectionCard: {
    marginTop: "0.65rem",
    padding: "0.8rem",
    borderRadius: "8px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.05)",
  },
  collectionName: {
    color: "var(--ot-text-primary, #e6edf3)",
    fontSize: "0.78rem",
    fontWeight: 600,
  },
  collectionDesc: {
    marginTop: "0.35rem",
    color: "var(--ot-text-secondary, #8b949e)",
    fontSize: "0.72rem",
    lineHeight: 1.5,
  },
  collectionMeta: {
    marginTop: "0.45rem",
    color: "var(--ot-text-muted, #6e7681)",
    fontSize: "0.66rem",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  },
  filter: {
    width: "240px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.55rem 0.65rem",
    fontSize: "0.78rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  nodeList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.6rem",
    marginTop: "0.9rem",
    maxHeight: "540px",
    overflowY: "auto",
  },
  nodeRow: {
    textAlign: "left",
    padding: "0.75rem 0.8rem",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.03)",
    cursor: "pointer",
  },
  nodeRowSelected: {
    border: "1px solid rgba(128,243,178,0.28)",
    background: "rgba(128,243,178,0.08)",
  },
  nodeTitle: {
    color: "var(--ot-text-primary, #e6edf3)",
    fontSize: "0.76rem",
    fontWeight: 600,
  },
  nodeMeta: {
    marginTop: "0.3rem",
    color: "var(--ot-text-secondary, #8b949e)",
    fontSize: "0.68rem",
  },
  empty: {
    color: "var(--ot-text-muted, #6e7681)",
    fontSize: "0.72rem",
    lineHeight: 1.6,
  },
};
